import {
  Badge,
  Button,
  Empty,
  Popover,
  Space,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  CloseOutlined,
  InfoCircleFilled,
  CheckCircleFilled,
  WarningFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  useNotificationsStore,
  selectUnreadCount,
  type AppNotification,
  type NotificationType,
} from '@/store/notifications';

function timeAgo(ts: number): string {
  const diffSec = Math.max(1, Math.round((Date.now() - ts) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const TYPE_ICON: Record<NotificationType, ReactNode> = {
  info: <InfoCircleFilled style={{ color: '#1677ff' }} />,
  success: <CheckCircleFilled style={{ color: '#52c41a' }} />,
  warning: <WarningFilled style={{ color: '#faad14' }} />,
  error: <CloseCircleFilled style={{ color: '#ff4d4f' }} />,
};

export function NotificationCenter() {
  const { token } = antdTheme.useToken();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = useNotificationsStore((s) => s.items);
  const unread = useNotificationsStore(selectUnreadCount);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const dismiss = useNotificationsStore((s) => s.dismiss);
  const clear = useNotificationsStore((s) => s.clear);

  const onRowClick = (n: AppNotification) => {
    markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  const content = (
    <div style={{ width: 360 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Typography.Text strong>Notifications</Typography.Text>
        <Space size={2}>
          <Button
            size="small"
            type="text"
            disabled={unread === 0}
            icon={<CheckOutlined />}
            onClick={() => markAllRead()}
          >
            Mark all read
          </Button>
          <Button
            size="small"
            type="text"
            disabled={items.length === 0}
            onClick={() => clear()}
          >
            Clear
          </Button>
        </Space>
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <div style={{ padding: '32px 12px' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="You're all caught up"
            />
          </div>
        ) : (
          items.map((n) => (
            <div
              key={n.id}
              role="button"
              tabIndex={0}
              onClick={() => onRowClick(n)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(n);
                }
              }}
              style={{
                display: 'flex',
                gap: 10,
                padding: '10px 12px',
                cursor: n.link ? 'pointer' : 'default',
                background: n.read ? 'transparent' : token.colorFillQuaternary,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ fontSize: 16, lineHeight: '20px', marginTop: 2 }}>
                {TYPE_ICON[n.type]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography.Text strong style={{ display: 'block' }}>
                  {n.title}
                </Typography.Text>
                {n.description && (
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 12, lineHeight: '18px' }}
                  >
                    {n.description}
                  </Typography.Text>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {timeAgo(n.createdAt)}
                </Typography.Text>
              </div>
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined />}
                aria-label="Dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  dismiss(n.id);
                }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
    >
      <Tooltip title="Notifications" placement="bottom">
        <Badge
          count={unread}
          size="small"
          offset={[-6, 6]}
          color={token.colorPrimary}
          overflowCount={99}
        >
          <Button type="text" size="large" icon={<BellOutlined />} aria-label="Notifications" />
        </Badge>
      </Tooltip>
    </Popover>
  );
}
